import {
  FleetAreaCode,
  fleetAreaCatalogSchema,
  patchFleetAreasResponseSchema,
  type FleetAreaCatalog,
  type FleetSwitchableArea,
  type PatchFleetAreasRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { fleetPolicyKey } from '@/products/fleet/features/use-fleet';

export const fleetAreasKey = ['fleet', 'areas'] as const;

/** `null` while the catalog is still loading. */
export function fleetAreaEnabled(
  catalog: FleetAreaCatalog | undefined,
  code: FleetSwitchableArea,
): boolean | null {
  if (!catalog) {
    return null;
  }
  return catalog.items.find((item) => item.code === code)?.enabled ?? true;
}

/** Missing catalog uses the product default: every area on, except telematics. */
export function fleetAreaOn(
  catalog: FleetAreaCatalog | undefined,
  code: FleetSwitchableArea,
): boolean {
  const stored = fleetAreaEnabled(catalog, code);
  if (stored === null) {
    return code !== FleetAreaCode.TELEMATICS;
  }
  return stored;
}

export function cachedFleetAreaOn(code: FleetSwitchableArea): boolean {
  const catalog = queryClient.getQueryData<FleetAreaCatalog>(fleetAreasKey);
  return fleetAreaOn(catalog, code);
}

export function useFleetAreas() {
  return useQuery({
    queryKey: fleetAreasKey,
    queryFn: () => apiFetch('/fleet/areas', fleetAreaCatalogSchema),
  });
}

export function usePatchFleetArea() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchFleetAreasRequest) =>
      apiFetch('/fleet/areas', patchFleetAreasResponseSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: fleetAreasKey });
      await queryClient.invalidateQueries({ queryKey: fleetPolicyKey });
    },
  });
}
